import { MetadataModules } from '../../../enums/index.js';
import * as metaData from '../../../metaData.js';
import genericViewportDisplaySetMetadataProvider from '../../../utilities/genericViewportDisplaySetMetadataProvider.js';
import { viewportProjection } from '../viewportProjection.js';
import { canvasToIndexForWSI } from './wsiTransformUtils.js';
import WSIViewport from './WSIViewport.js';
class WSIViewportLegacyAdapter extends WSIViewport {
    setCamera(cameraPatch) {
        const viewStatePatch = {};
        if (typeof cameraPatch.zoom === 'number') {
            viewStatePatch.zoom = cameraPatch.zoom;
        }
        if (cameraPatch.centerIndex) {
            viewStatePatch.centerIndex = [
                cameraPatch.centerIndex[0],
                cameraPatch.centerIndex[1],
            ];
        }
        if (typeof cameraPatch.rotation === 'number') {
            viewStatePatch.rotation = cameraPatch.rotation;
        }
        if (typeof cameraPatch.resolution === 'number') {
            viewStatePatch.resolution = cameraPatch.resolution;
        }
        if (typeof cameraPatch.parallelScale === 'number') {
            const spacing = this.getImageData()?.spacing?.[0];
            if (typeof spacing === 'number' && spacing > 0) {
                const worldToCanvasRatio = this.element.clientHeight /
                    Math.max(cameraPatch.parallelScale, 0.001);
                viewStatePatch.resolution = 1 / spacing / worldToCanvasRatio;
            }
        }
        if (cameraPatch.focalPoint) {
            const view = this.getView();
            if (view) {
                const newCanvas = this.worldToCanvas(cameraPatch.focalPoint);
                const newIndex = canvasToIndexForWSI({
                    canvasPos: newCanvas,
                    canvasWidth: this.element.clientWidth,
                    canvasHeight: this.element.clientHeight,
                    view,
                });
                viewStatePatch.centerIndex = [newIndex[0], newIndex[1]];
            }
        }
        if (Object.keys(viewStatePatch).length) {
            this.setViewState(viewStatePatch);
        }
    }
    getCamera() {
        return {
            ...this.getViewState(),
            ...this.getCameraForEvent(),
        };
    }
    setDataIds(imageIds, options) {
        const dataId = imageIds[0];
        if (!dataId) {
            return;
        }
        const webClient = options?.webClient ||
            metaData.get(MetadataModules.WADO_WEB_CLIENT, dataId);
        if (!webClient) {
            throw new Error(`To use setDataIds on WSI data, you must provide metaData.webClient for ${dataId}.`);
        }
        genericViewportDisplaySetMetadataProvider.add(dataId, {
            imageIds,
            kind: 'wsi',
            options: {
                miniNavigationOverlay: options?.miniNavigationOverlay,
                webClient,
            },
        });
        return this.setDisplaySets({ displaySetId: dataId });
    }
    async setWSI(imageIds, client) {
        return this.setDataIds(imageIds, {
            webClient: client,
        });
    }
    setProperties(props) {
        const dataId = this.getActiveDataId();
        if (!dataId) {
            return;
        }
        this.setDisplaySetPresentation(dataId, {
            voiRange: props.voiRange,
        });
    }
    getProperties() {
        const dataId = this.getActiveDataId();
        const dataPresentation = dataId
            ? this.getDisplaySetPresentation(dataId)
            : undefined;
        return {
            voiRange: dataPresentation?.voiRange,
        };
    }
    resetProperties() {
        const dataId = this.getActiveDataId();
        if (!dataId) {
            return;
        }
        this.setDisplaySetPresentation(dataId, {
            averageWhite: undefined,
            voiRange: {
                lower: 0,
                upper: 255,
            },
        });
    }
    getViewPresentation(selector) {
        return viewportProjection.getPresentation(this, {
            selector,
        });
    }
    setViewPresentation(viewPres) {
        if (!viewPres) {
            return;
        }
        const nextViewState = viewportProjection.withPresentation(this, viewPres);
        if (nextViewState) {
            this.setViewState(nextViewState);
        }
    }
    resetCamera() {
        return this.resetViewState();
    }
    setVOI(voiRange) {
        const dataId = this.getActiveDataId();
        if (!dataId) {
            return;
        }
        this.setDisplaySetPresentation(dataId, { voiRange });
    }
    setAverageWhite(averageWhite) {
        const dataId = this.getActiveDataId();
        if (!dataId) {
            return;
        }
        this.setDisplaySetPresentation(dataId, { averageWhite });
    }
}
export default WSIViewportLegacyAdapter;
